<template>
  <Transition name="fade">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      @click.self="emit('close')"
    >
      <div
        class="relative flex h-full max-h-[80vh] min-h-0 w-full max-w-[1120px] gap-3 md:gap-8 overflow-hidden rounded-[20px] border border-tertiary bg-secondary p-3 md:p-8 shadow-2xl"
      >
        <!-- Linke Seite: Liste der Gruppen -->
        <div class="flex w-[320px] shrink-0 flex-col gap-6">
          <div class="flex items-center justify-between">
            <h2 class="text-2xl font-bold">Meine Gruppen</h2>
          </div>

          <div class="flex flex-col gap-2 overflow-y-auto">
            <button
              v-for="group in groups"
              :key="group.id"
              class="flex items-center justify-between rounded-[20px] border p-4 text-left transition-colors"
              :class="[
                selectedGroup?.id === group.id
                  ? 'border-accent bg-accent/10'
                  : 'border-tertiary bg-primary hover:border-text-main/20',
              ]"
              @click="selectGroup(group)"
            >
              <span class="font-bold">{{ group.name }}</span>
            </button>
          </div>

          <button
            class="mt-auto flex h-12 w-full items-center justify-center rounded-xl bg-accent font-bold text-white transition-colors hover:bg-accent/80"
            @click="createNewGroupForm"
          >
            + Neue Gruppe erstellen
          </button>
        </div>

        <!-- Rechte Seite: Gruppe erstellen ODER Gruppe bearbeiten -->
        <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-6 rounded-[20px] border border-tertiary bg-primary p-6">
          
          <!-- State: Loading -->
          <div v-if="isLoading" class="flex h-full items-center justify-center">
            <span class="text-text-main/50">Lade...</span>
          </div>
          
          <!-- State: No Selection -->
          <div v-else-if="!isCreatingGroup && !selectedGroup" class="flex h-full items-center justify-center">
            <span class="text-text-main/50">Wähle eine Gruppe aus oder erstelle eine neue.</span>
          </div>

          <!-- State: Create New Group -->
          <div v-else-if="isCreatingGroup" class="flex flex-col gap-4">
            <h3 class="text-xl font-bold">Neue Gruppe erstellen</h3>
            
            <div class="flex flex-col gap-2">
              <label class="text-sm font-bold text-text-main/70">Gruppenname</label>
              <input
                v-model="newGroupData.name"
                type="text"
                class="rounded-lg border border-tertiary bg-secondary p-3 text-text-main focus:border-accent focus:outline-none"
                placeholder="Name der Gruppe..."
              />
            </div>
            
            <div class="flex flex-col gap-2">
              <label class="text-sm font-bold text-text-main/70">Gültig von (optional)</label>
              <input
                v-model="newGroupData.valid_from"
                type="datetime-local"
                class="rounded-lg border border-tertiary bg-secondary p-3 text-text-main focus:border-accent focus:outline-none"
              />
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-bold text-text-main/70">Gültig bis (optional)</label>
              <input
                v-model="newGroupData.valid_until"
                type="datetime-local"
                class="rounded-lg border border-tertiary bg-secondary p-3 text-text-main focus:border-accent focus:outline-none"
              />
            </div>

            <div class="flex flex-col gap-2">
              <label class="flex items-center gap-2 text-sm font-bold text-text-main/70">
                <input
                  v-model="newGroupData.is_exam"
                  type="checkbox"
                  class="h-4 w-4 accent-accent"
                />
                Ist eine Prüfungsgruppe
              </label>
            </div>

            <div class="mt-4 flex gap-4">
              <button
                class="flex h-10 flex-1 items-center justify-center rounded-lg border border-tertiary bg-secondary font-bold transition-colors hover:bg-tertiary"
                @click="cancelCreate"
              >
                Abbrechen
              </button>
              <button
                class="flex h-10 flex-1 items-center justify-center rounded-lg bg-accent font-bold text-white transition-colors hover:bg-accent/80"
                @click="submitCreateGroup"
                :disabled="!newGroupData.name"
                :class="{ 'opacity-50': !newGroupData.name }"
              >
                Erstellen
              </button>
            </div>
          </div>

          <!-- State: Edit Group -->
          <div v-else-if="selectedGroup" class="flex h-full flex-col gap-6 overflow-hidden">
            <h3 class="text-xl font-bold">{{ selectedGroup.name }} verwalten</h3>
            
            <!-- Mitglieder hinzufügen -->
            <div class="flex flex-col gap-2">
              <label class="text-sm font-bold text-text-main">Mitglieder suchen & hinzufügen</label>
              <div class="flex gap-2">
                <input
                  v-model="searchQuery"
                  type="text"
                  class="flex-1 rounded-lg border border-tertiary bg-secondary p-3 text-text-main focus:border-accent focus:outline-none"
                  placeholder="Nach Name oder E-Mail suchen..."
                  @keyup.enter="searchUsers"
                />
                <button
                  class="flex w-24 items-center justify-center rounded-lg bg-accent font-bold text-white transition-colors hover:bg-accent/80"
                  @click="searchUsers"
                >
                  Suchen
                </button>
              </div>
              
              <!-- Suchergebnisse -->
              <div v-if="searchResults.length > 0" class="mt-2 flex max-h-32 flex-col gap-2 overflow-y-auto rounded-lg border border-tertiary p-2">
                <div v-for="user in searchResults" :key="user.id" class="flex items-center justify-between rounded p-2 hover:bg-secondary">
                  <span>{{ user.name }} {{ user.surname }} ({{ user.email }})</span>
                  <button
                    class="rounded bg-accent px-3 py-1 text-sm font-bold text-white hover:bg-accent/80"
                    @click="addUserToGroup(user.id)"
                  >
                    Hinzufügen
                  </button>
                </div>
              </div>
              <div v-else-if="hasSearched" class="mt-2 text-sm text-text-main/50">
                Keine passenden Nutzer gefunden.
              </div>
            </div>

            <!-- Mitgliederliste -->
            <div class="flex min-h-0 flex-col gap-2">
              <label class="text-sm font-bold text-text-main">Aktuelle Mitglieder ({{ groupMembers.length }})</label>
              <div class="flex-1 overflow-y-auto rounded-lg border border-tertiary bg-secondary p-2">
                <div v-if="groupMembers.length === 0" class="p-4 text-center text-text-main/50">
                  Noch keine Mitglieder in dieser Gruppe.
                </div>
                <div
                  v-for="member in groupMembers"
                  :key="member.user_id"
                  class="flex items-center gap-3 border-b border-tertiary p-3 last:border-0"
                >
                  <div class="h-8 w-8 flex items-center justify-center rounded-full bg-primary font-bold text-white">
                    {{ member.user?.name?.charAt(0) || '?' }}{{ member.user?.surname?.charAt(0) || '?' }}
                  </div>
                  <div>
                    <span class="font-bold">{{ member.user?.name }} {{ member.user?.surname }}</span>
                    <span class="ml-2 text-sm text-text-main/60">({{ member.user?.email }})</span>
                  </div>
                  <button
                    class="ml-auto text-red-500 hover:text-red-400"
                    @click="removeUserFromGroup(member.user_id)"
                  >
                    Entfernen
                  </button>
                </div>
              </div>
            </div>

            <!-- Gruppe an Workspace binden -->
            <div class="flex flex-col gap-2 mt-4 pt-4 border-t border-tertiary">
              <label class="text-sm font-bold text-text-main/70">An Projekt / Namespace binden</label>
              <div class="flex gap-2">
                <select
                  v-model="bindNamespaceId"
                  class="flex-1 rounded-lg border border-tertiary bg-secondary p-3 text-text-main focus:border-accent focus:outline-none"
                >
                  <option value="" disabled>Namespace / Projekt auswählen...</option>
                  <option v-for="ns in availableNamespaces" :key="ns.id" :value="ns.id">
                    {{ ns.name }}
                  </option>
                </select>
                <button
                  class="flex w-24 items-center justify-center rounded-lg bg-accent font-bold text-white transition-colors hover:bg-accent/80"
                  @click="attachToNamespace"
                >
                  Binden
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { API_BASE_URL } from '@/config'
import { pushToast } from '@/composables/useToast'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const isLoading = ref(false)
const groups = ref<Record<string, unknown>[]>([])
const selectedGroup = ref<Record<string, unknown> | null>(null)
const isCreatingGroup = ref(false)

const newGroupData = ref({
  name: '',
  valid_from: '',
  valid_until: '',
  is_exam: false,
})

const searchQuery = ref('')
const searchResults = ref<Record<string, unknown>[]>([])
const hasSearched = ref(false)
const groupMembers = ref<Record<string, unknown>[]>([])

const bindNamespaceId = ref('')
const availableNamespaces = ref<{id: string, name: string}[]>([])

async function loadAvailableNamespaces() {
  try {
    const nsRes = await fetch(`${API_BASE_URL}/api/users/namespace`, { credentials: 'include' })
    if (nsRes.ok) {
      const ns = await nsRes.json()
      if (ns && ns.id) {
        availableNamespaces.value.push({ id: ns.id, name: `Mein Workspace (${ns.name})` })
      }
    }
    const projectsRes = await fetch(`${API_BASE_URL}/api/users/namespace/projects`, { credentials: 'include' })
    if (projectsRes.ok) {
      const projects = await projectsRes.json()
      for (const p of projects) {
        if (p.namespace_id) {
          availableNamespaces.value.push({ id: p.namespace_id, name: `Projekt: ${p.title || p.name || 'Ohne Titel'}` })
        }
      }
    }
  } catch (error) {
    console.error('Failed to load namespaces', error)
  }
}

// Beim Öffnen des Overlays die Gruppen laden
watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      loadGroups()
      availableNamespaces.value = []
      loadAvailableNamespaces()
    } else {
      resetState()
    }
  }
)

function resetState() {
  selectedGroup.value = null
  isCreatingGroup.value = false
  searchQuery.value = ''
  searchResults.value = []
  hasSearched.value = false
  groupMembers.value = []
  bindNamespaceId.value = ''
}

async function loadGroups() {
  isLoading.value = true
  try {
    const res = await fetch(`${API_BASE_URL}/api/groups/instructor-groups`, {
      credentials: 'include',
    })
    if (res.ok) {
      groups.value = await res.json()
    }
  } catch (error) {
    console.error('Error loading groups:', error)
  } finally {
    isLoading.value = false
  }
}

async function selectGroup(group: Record<string, unknown>) {
  isCreatingGroup.value = false
  selectedGroup.value = group
  searchQuery.value = ''
  searchResults.value = []
  hasSearched.value = false
  await loadGroupMembers(group.id)
}

function createNewGroupForm() {
  selectedGroup.value = null
  isCreatingGroup.value = true
  newGroupData.value = {
    name: '',
    valid_from: '',
    valid_until: '',
    is_exam: false,
  }
}

function cancelCreate() {
  isCreatingGroup.value = false
}

async function submitCreateGroup() {
  if (!newGroupData.value.name) return

  isLoading.value = true
  try {
    const payload: Record<string, unknown> = {
      name: newGroupData.value.name,
    }
    
    // Convert to ISO string if provided
    if (newGroupData.value.valid_from) {
      payload.valid_from = new Date(newGroupData.value.valid_from).toISOString()
    }
    if (newGroupData.value.valid_until) {
      payload.valid_until = new Date(newGroupData.value.valid_until).toISOString()
    }
    
    payload.is_exam = newGroupData.value.is_exam

    const res = await fetch(`${API_BASE_URL}/api/groups/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    })

    if (!res.ok) throw new Error('Fehler beim Erstellen der Gruppe')

    pushToast('success', 'Gruppe erfolgreich erstellt!')
    await loadGroups()
    isCreatingGroup.value = false
    // Automatisch die neue Gruppe auswählen (optional, hier einfach nullen)
    selectedGroup.value = null
  } catch (error) {
    console.error(error)
    pushToast('error', error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten')
  } finally {
    isLoading.value = false
  }
}

async function searchUsers() {
  if (!searchQuery.value.trim()) return
  
  try {
    const res = await fetch(`${API_BASE_URL}/api/users/search?query=${encodeURIComponent(searchQuery.value)}`, {
      credentials: 'include',
    })
    if (res.ok) {
      searchResults.value = await res.json()
      hasSearched.value = true
    }
  } catch (error) {
    console.error(error)
  }
}

async function loadGroupMembers(groupId: string) {
  try {
    const res = await fetch(`${API_BASE_URL}/api/groups/getAllGroupMembers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ group_id: groupId }),
    })
    if (res.ok) {
      groupMembers.value = await res.json()
    }
  } catch (error) {
    console.error(error)
  }
}

async function addUserToGroup(userId: string) {
  if (!selectedGroup.value) return
  
  try {
    const res = await fetch(`${API_BASE_URL}/api/groups/addUser`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ group_id: selectedGroup.value.id, user_id: userId }),
    })
    if (!res.ok) throw new Error('Fehler beim Hinzufügen')
    
    pushToast('success', 'Nutzer hinzugefügt')
    await loadGroupMembers(selectedGroup.value.id)
    // Remove from search results visually
    searchResults.value = searchResults.value.filter(u => u.id !== userId)
  } catch (error) {
    console.error(error)
    pushToast('error', error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten')
  }
}

async function removeUserFromGroup(userId: string) {
  if (!selectedGroup.value) return
  
  try {
    const res = await fetch(`${API_BASE_URL}/api/groups/removeUser`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ group_id: selectedGroup.value.id, user_id: userId }),
    })
    if (!res.ok) throw new Error('Fehler beim Entfernen')

    pushToast('success', 'Nutzer entfernt')
    await loadGroupMembers(selectedGroup.value.id)
  } catch (error) {
    console.error(error)
    pushToast('error', error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten')
  }
}

async function attachToNamespace() {
  if (!selectedGroup.value || !bindNamespaceId.value.trim()) return

  try {
    const res = await fetch(`${API_BASE_URL}/api/groups/attach-to-namespace`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        group_id: selectedGroup.value.id,
        namespace_id: bindNamespaceId.value.trim(),
        role: "student"
      }),
    })
    if (!res.ok) throw new Error('Fehler beim Binden der Gruppe an den Namespace')

    pushToast('success', 'Gruppe erfolgreich an Namespace gebunden!')
    bindNamespaceId.value = ''
  } catch (error) {
    console.error(error)
    pushToast('error', error instanceof Error ? error.message : 'Ein Fehler ist aufgetreten')
  }
}
</script>
